import React, { useState, useEffect } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title } from 'chart.js';
import { Pie, Line, Bar } from 'react-chartjs-2';
import { portfolioAPI } from '../utils/api';
import { toast } from 'react-hot-toast';
import {
  Box,
  Heading,
  Text,
  SimpleGrid,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Badge,
  Container,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  HStack,
  Select,
  Card,
  CardBody,
  CardHeader,
  Icon,
  Button,
  VStack
} from '@chakra-ui/react';
import {
  FiArrowUp,
  FiArrowDown,
  FiBarChart2,
  FiActivity,
  FiDollarSign,
  FiUsers,
  FiPieChart,
  FiTrendingUp,
  FiTrendingDown,
  FiClock,
  FiCalendar
} from 'react-icons/fi';
import './Portfolio.css';

// Register Chart.js components
ChartJS.register(
  ArcElement, 
  Tooltip, 
  Legend, 
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  BarElement, 
  Title
);

// Mock data for when API fails
const mockPortfolioData = {
  totalValue: 1250000,
  pnl: 15000,
  pnlPercentage: 13.6,
  treasuryChange: 5.8,
  tradeVolume: 875000,
  tradeVolumeChange: 12.4,
  activeUsers: 845,
  activeUsersChange: 8.9,
  newProposals: 32,
  newProposalsChange: 15.3,
  categories: {
    longTerm: 650000,
    highRisk: 450000,
    staked: 150000
  },
  tokens: [
    { name: 'BONK', value: 350000, change: 22.5 },
    { name: 'TRUMP', value: 250000, change: -5.2 },
    { name: 'BERN', value: 200000, change: 12.8 },
    { name: 'DESOL', value: 150000, change: 8.3 },
    { name: 'SOL', value: 300000, change: 3.7 }
  ],
  topPerformers: [
    { token: 'BONK', change: 25.8 },
    { token: 'DESOL', change: 15.2 },
    { token: 'WOOF', change: 12.7 }
  ],
  worstPerformers: [
    { token: 'BERN', change: -12.5 },
    { token: 'TRUMP', change: -8.3 },
    { token: 'JTO', change: -3.7 }
  ],
  historicalData: {
    dates: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep'],
    treasuryValue: [800000, 850000, 920000, 890000, 950000, 1050000, 1100000, 1180000, 1250000],
    tradeVolume: [320000, 380000, 420000, 390000, 450000, 510000, 580000, 650000, 875000]
  }
};

const Portfolio = () => {
  const [portfolioData, setPortfolioData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('weekly');

  // Move all color mode values to the top
  const cardBg = useColorModeValue('white', 'dark.card');
  const borderColor = useColorModeValue('gray.200', 'dark.border');
  const statBg = useColorModeValue('blue.50', 'rgba(118, 120, 237, 0.1)');
  const textColor = useColorModeValue('gray.800', 'dark.textPrimary');
  const secondaryTextColor = useColorModeValue('gray.600', 'dark.textSecondary');

  useEffect(() => {
    const fetchPortfolioData = async () => {
      try {
        // Try to fetch real data from API
        const data = await portfolioAPI.getPortfolio();
        setPortfolioData(data);
      } catch (error) {
        console.error('Error fetching portfolio data:', error);
        // Fall back to mock data when API fails
        setPortfolioData(mockPortfolioData);
        toast.error('Failed to fetch data from API, showing demo data');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPortfolioData();
  }, [timeframe]);

  // Prepare allocation chart data
  const allocationChartData = {
    labels: ['Long-Term', 'High-Risk', 'Staked'],
    datasets: [
      {
        data: portfolioData ? [
          portfolioData.categories.longTerm,
          portfolioData.categories.highRisk,
          portfolioData.categories.staked
        ] : [0, 0, 0],
        backgroundColor: [
          'rgba(0, 255, 157, 0.6)',
          'rgba(255, 0, 102, 0.6)',
          'rgba(255, 204, 0, 0.6)'
        ],
        borderColor: [
          'rgba(0, 255, 157, 1)',
          'rgba(255, 0, 102, 1)',
          'rgba(255, 204, 0, 1)'
        ],
        borderWidth: 1,
      },
    ],
  };

  // Prepare historical trend chart data
  const trendChartData = {
    labels: portfolioData?.historicalData.dates || [],
    datasets: [
      {
        label: 'Treasury Value',
        data: portfolioData?.historicalData.treasuryValue || [],
        borderColor: 'rgba(118, 120, 237, 1)',
        backgroundColor: 'rgba(118, 120, 237, 0.2)',
        tension: 0.3,
        fill: true
      }
    ]
  };
  
  // Prepare volume chart data
  const volumeChartData = {
    labels: portfolioData?.historicalData.dates || [],
    datasets: [
      {
        label: 'Trade Volume',
        data: portfolioData?.historicalData.tradeVolume || [],
        backgroundColor: 'rgba(0, 255, 157, 0.6)',
        borderColor: 'rgba(0, 255, 157, 1)',
        borderWidth: 1,
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          color: textColor,
          font: {
            family: 'Inter',
            size: window.innerWidth < 768 ? 10 : 12
          },
          boxWidth: window.innerWidth < 768 ? 10 : 15
        }
      },
      tooltip: {
        bodyFont: {
          size: window.innerWidth < 768 ? 10 : 12
        },
        titleFont: {
          size: window.innerWidth < 768 ? 12 : 14
        }
      }
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10
          }
        }
      },
      y: {
        ticks: {
          font: {
            size: window.innerWidth < 768 ? 8 : 10
          }
        }
      }
    }
  };

  if (isLoading) {
    return (
      <Flex justify="center" align="center" height="50vh" direction="column">
        <Spinner size="xl" color="brand.400" thickness="4px" speed="0.65s" mb={4} />
        <Text color={secondaryTextColor}>Loading dashboard data...</Text>
      </Flex>
    );
  }

  return (
    <Container maxW="container.xl" py={4} px={{ base: 2, md: 4 }}>
      <Flex 
        direction={{ base: "column", sm: "row" }} 
        justify="space-between" 
        align={{ base: "flex-start", sm: "center" }} 
        mb={6}
        gap={4}
      >
        <Box>
          <Heading as="h1" size={{ base: "lg", md: "xl" }} color={textColor}>Dashboard</Heading>
          <Text color={secondaryTextColor} mt={1} fontSize={{ base: "sm", md: "md" }}>Treasury performance metrics and portfolio analytics</Text>
        </Box>
        
        <HStack spacing={2}>
          <Text color={secondaryTextColor} fontSize={{ base: "sm", md: "md" }}>Timeframe:</Text>
          <Select 
            value={timeframe} 
            onChange={(e) => setTimeframe(e.target.value)}
            width={{ base: "120px", md: "150px" }}
            size={{ base: "sm", md: "md" }}
            bg={cardBg}
            borderColor={borderColor}
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="yearly">Yearly</option>
          </Select>
        </HStack>
      </Flex>
      
      {/* Key Metrics */}
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6} mb={8}>
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody>
            <Stat>
              <StatLabel color={secondaryTextColor}>
                <Flex align="center">
                  <Icon as={FiDollarSign} mr={2} />
                  Treasury Value
                </Flex>
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                ${portfolioData.totalValue.toLocaleString()}
              </StatNumber>
              <StatHelpText>
                <StatArrow type={portfolioData.treasuryChange >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(portfolioData.treasuryChange)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody>
            <Stat>
              <StatLabel color={secondaryTextColor}>
                <Flex align="center">
                  <Icon as={FiBarChart2} mr={2} />
                  Trade Volume
                </Flex>
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                ${portfolioData.tradeVolume.toLocaleString()}
              </StatNumber>
              <StatHelpText>
                <StatArrow type={portfolioData.tradeVolumeChange >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(portfolioData.tradeVolumeChange)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody>
            <Stat>
              <StatLabel color={secondaryTextColor}>
                <Flex align="center">
                  <Icon as={FiUsers} mr={2} />
                  Active Users
                </Flex>
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                {portfolioData.activeUsers.toLocaleString()}
              </StatNumber>
              <StatHelpText>
                <StatArrow type={portfolioData.activeUsersChange >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(portfolioData.activeUsersChange)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
        
        <Card bg={cardBg} borderColor={borderColor} borderWidth="1px" boxShadow="sm">
          <CardBody>
            <Stat>
              <StatLabel color={secondaryTextColor}>
                <Flex align="center">
                  <Icon as={FiActivity} mr={2} />
                  New Proposals
                </Flex>
              </StatLabel>
              <StatNumber color={textColor} fontSize="2xl">
                {portfolioData.newProposals.toLocaleString()}
              </StatNumber>
              <StatHelpText>
                <StatArrow type={portfolioData.newProposalsChange >= 0 ? 'increase' : 'decrease'} />
                {Math.abs(portfolioData.newProposalsChange)}%
              </StatHelpText>
            </Stat>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Performance Charts */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6} mb={8}>
        <Card 
          bg={cardBg} 
          borderColor={borderColor} 
          borderWidth="1px" 
          boxShadow="sm"
        >
          <CardHeader>
            <Heading as="h2" size="md" color={textColor}>
              <Flex align="center">
                <Icon as={FiTrendingUp} mr={2} />
                Treasury Performance
              </Flex>
            </Heading>
          </CardHeader>
          <CardBody>
            <Box height={{ base: "250px", md: "300px" }} position="relative" className="chart-container">
              <Line data={trendChartData} options={chartOptions} />
            </Box>
          </CardBody>
        </Card>

        <Card 
          bg={cardBg} 
          borderColor={borderColor} 
          borderWidth="1px" 
          boxShadow="sm"
        >
          <CardHeader>
            <Heading as="h2" size="md" color={textColor}>
              <Flex align="center">
                <Icon as={FiBarChart2} mr={2} />
                Trading Volume
              </Flex>
            </Heading>
          </CardHeader>
          <CardBody>
            <Box height={{ base: "250px", md: "300px" }} position="relative" className="chart-container">
              <Bar data={volumeChartData} options={chartOptions} />
            </Box>
          </CardBody>
        </Card>
      </SimpleGrid>

      {/* Portfolio Details */}
      <Tabs colorScheme="brand" variant="enclosed" mb={6} isLazy>
        <TabList overflowX="auto" overflowY="hidden" display="flex" flexWrap={{ base: "nowrap", md: "wrap" }} width="100%">
          <Tab minWidth={{ base: "120px", md: "auto" }} whiteSpace="nowrap">
            <Icon as={FiPieChart} mr={{ base: 1, md: 2 }} />
            <Text display={{ base: "none", sm: "inline" }}>Allocation</Text>
          </Tab>
          <Tab minWidth={{ base: "120px", md: "auto" }} whiteSpace="nowrap">
            <Icon as={FiDollarSign} mr={{ base: 1, md: 2 }} />
            <Text display={{ base: "none", sm: "inline" }}>Holdings</Text>
          </Tab>
          <Tab minWidth={{ base: "120px", md: "auto" }} whiteSpace="nowrap">
            <Icon as={FiTrendingUp} mr={{ base: 1, md: 2 }} />
            <Text display={{ base: "none", sm: "inline" }}>Top Performers</Text>
          </Tab>
          <Tab minWidth={{ base: "120px", md: "auto" }} whiteSpace="nowrap">
            <Icon as={FiTrendingDown} mr={{ base: 1, md: 2 }} />
            <Text display={{ base: "none", sm: "inline" }}>Worst Performers</Text>
          </Tab>
        </TabList>
        
        <TabPanels>
          <TabPanel px={0} py={4}>
            <Card 
              bg={cardBg} 
              borderColor={borderColor} 
              borderWidth="1px" 
              boxShadow="sm"
            >
              <CardHeader>
                <Heading as="h2" size="md" color={textColor}>Portfolio Allocation</Heading>
              </CardHeader>
              <CardBody>
                <Flex 
                  direction={{ base: 'column', md: 'row' }}
                  align="center"
                  justify="space-around"
                >
                  <Box width={{ base: '100%', md: '50%' }} height={{ base: "220px", md: "300px" }} position="relative" className="chart-container">
                    <Pie data={allocationChartData} options={chartOptions} />
                  </Box>
                  
                  <VStack 
                    width={{ base: '100%', md: '40%' }} 
                    spacing={{ base: 3, md: 4 }} 
                    mt={{ base: 4, md: 0 }}
                    align="stretch"
                  >
                    <Stat bg={statBg} p={{ base: 3, md: 4 }} borderRadius="md">
                      <StatLabel fontSize={{ base: "sm", md: "md" }}>Long-Term</StatLabel>
                      <StatNumber fontSize={{ base: "lg", md: "xl" }}>${portfolioData.categories.longTerm.toLocaleString()}</StatNumber>
                      <StatHelpText fontSize={{ base: "xs", md: "sm" }}>{Math.round(portfolioData.categories.longTerm / portfolioData.totalValue * 100)}% of portfolio</StatHelpText>
                    </Stat>
                    
                    <Stat bg={statBg} p={{ base: 3, md: 4 }} borderRadius="md">
                      <StatLabel fontSize={{ base: "sm", md: "md" }}>High-Risk</StatLabel>
                      <StatNumber fontSize={{ base: "lg", md: "xl" }}>${portfolioData.categories.highRisk.toLocaleString()}</StatNumber>
                      <StatHelpText fontSize={{ base: "xs", md: "sm" }}>{Math.round(portfolioData.categories.highRisk / portfolioData.totalValue * 100)}% of portfolio</StatHelpText>
                    </Stat>
                    
                    <Stat bg={statBg} p={{ base: 3, md: 4 }} borderRadius="md">
                      <StatLabel fontSize={{ base: "sm", md: "md" }}>Staked</StatLabel>
                      <StatNumber fontSize={{ base: "lg", md: "xl" }}>${portfolioData.categories.staked.toLocaleString()}</StatNumber>
                      <StatHelpText fontSize={{ base: "xs", md: "sm" }}>{Math.round(portfolioData.categories.staked / portfolioData.totalValue * 100)}% of portfolio</StatHelpText>
                    </Stat>
                  </VStack>
                </Flex>
              </CardBody>
            </Card>
          </TabPanel>
          
          <TabPanel px={0} py={4}>
            <Card 
              bg={cardBg} 
              borderColor={borderColor} 
              borderWidth="1px" 
              boxShadow="sm"
            >
              <CardHeader>
                <Heading as="h2" size="md" color={textColor}>Token Holdings</Heading>
              </CardHeader>
              <CardBody>
                <Box overflowX="auto">
                  <Table variant="simple" size={{ base: "sm", md: "md" }} className="responsive-table">
                    <Thead bg={statBg}>
                      <Tr>
                        <Th>Token</Th>
                        <Th isNumeric>Value (USD)</Th>
                        <Th isNumeric>% of Portfolio</Th>
                        <Th isNumeric>24h Change</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
              {portfolioData.tokens.map((token, index) => (
                        <Tr key={index}>
                          <Td>
                            <Badge colorScheme="brand" variant="subtle" px={2} py={1} borderRadius="full">
                              {token.name}
                            </Badge>
                          </Td>
                          <Td isNumeric>${token.value.toLocaleString()}</Td>
                          <Td isNumeric>{Math.round(token.value / portfolioData.totalValue * 100)}%</Td>
                          <Td isNumeric>
                            <Text color={token.change >= 0 ? 'green.500' : 'red.500'} fontWeight="bold">
                    {token.change >= 0 ? '+' : ''}{token.change}%
                            </Text>
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </Table>
                </Box>
              </CardBody>
            </Card>
          </TabPanel>
          
          <TabPanel px={0} py={4}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {portfolioData.topPerformers.map((token, index) => (
                <Card 
                  key={index} 
                  bg={cardBg} 
                  borderColor={borderColor} 
                  borderWidth="1px" 
                  boxShadow="sm"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                  transition="transform 0.2s"
                >
                  <CardBody>
                    <Flex justify="space-between" align="center">
                      <Heading size="md" color={textColor}>{token.token}</Heading>
                      <Badge colorScheme="green" fontSize="lg" px={2} py={1}>
                        <Flex align="center">
                          <Icon as={FiTrendingUp} mr={1} />
                          +{token.change}%
                        </Flex>
                      </Badge>
                    </Flex>
                    <Text color={secondaryTextColor} mt={2}>Top {index + 1} performer this period</Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </TabPanel>
          
          <TabPanel px={0} py={4}>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              {portfolioData.worstPerformers.map((token, index) => (
                <Card 
                  key={index} 
                  bg={cardBg} 
                  borderColor={borderColor} 
                  borderWidth="1px" 
                  boxShadow="sm"
                  _hover={{ transform: 'translateY(-2px)', boxShadow: 'md' }}
                  transition="transform 0.2s"
                >
                  <CardBody>
                    <Flex justify="space-between" align="center">
                      <Heading size="md" color={textColor}>{token.token}</Heading>
                      <Badge colorScheme="red" fontSize="lg" px={2} py={1}>
                        <Flex align="center">
                          <Icon as={FiTrendingDown} mr={1} />
                          {token.change}%
                        </Flex>
                      </Badge>
                    </Flex>
                    <Text color={secondaryTextColor} mt={2}>Worst {index + 1} performer this period</Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </TabPanel>
        </TabPanels>
      </Tabs>
      
      <Box mb={6}>
        <Button 
          leftIcon={<FiBarChart2 />} 
          colorScheme="brand"
          size="lg"
          width="100%"
        >
          Generate Detailed Analytics Report
        </Button>
      </Box>
    </Container>
  );
};

export default Portfolio;